import { useCallback } from "react";
import { useAuth } from "../../../context/AuthContexts";

/**
 * Whose store is whose.
 *
 * A store is designated to an admin, and only they — or a super admin — move
 * stock in or out of it. The API enforces that; this is so the screens do not
 * offer buttons that will come back 403, which is a worse way to learn a rule
 * than not being shown it.
 *
 * Matched on the custodian's USER id rather than their admin id, because the
 * session knows who is signed in as a user and the store carries its
 * custodian's user_id with it. The API does the same comparison from the other
 * direction.
 */
export function useStoreCustody() {
  const { user, hasRole } = useAuth();

  const isSuperAdmin = hasRole("super_admin");

  const isCustodianOf = useCallback(
    (store: any) => {
      const custodianUserId = store?.custodian?.user_id ?? store?.custodian?.user?.id;
      return !!custodianUserId && String(custodianUserId) === String((user as any)?.id);
    },
    [user],
  );

  /** May the signed-in user move stock in or out of this store? */
  const canWork = useCallback(
    (store: any) => isSuperAdmin || isCustodianOf(store),
    [isSuperAdmin, isCustodianOf],
  );

  /** Why not — in words that name who to go to. */
  const refusal = useCallback((store: any) => {
    if (!store?.custodian) {
      return `${store?.name ?? "This store"} has no custodian, so only a super admin can move stock in or out of it.`;
    }
    const name =
      [store.custodian.firstname, store.custodian.lastname].filter(Boolean).join(" ") ||
      store.custodian.user?.name ||
      "its custodian";
    return `${store.name} is ${name}'s store — only they, or a super admin, can move its stock.`;
  }, []);

  return { isSuperAdmin, isCustodianOf, canWork, refusal };
}
